/**
 * Per-feature icons for the B2B dashboard grid, keyed by the `key` in
 * utils/dashboardFeatures.js.
 *
 * Separate from STAT_ICONS on purpose. STAT_ICONS is keyed by the *kind of
 * metric* — "sessions", "users", "score" — so that the same measurement wears
 * the same icon on every hub, and it is shared by dozens of stat cards. A
 * feature card is not a metric: two features can both report a score and still
 * need to be told apart at a glance. Pointing the grid at STAT_ICONS gave all
 * six care programmes one User-Group icon, all three scans one AI-Spark, and
 * three different keys (`completions`, `score`, `personalBest`) that resolve to
 * the identical Target file. Anything renamed here is invisible to the stat
 * cards, and vice versa.
 *
 * Every icon is a flat #000000 stroke on `fill="none"`, rendered through
 * <img> and flipped for dark mode by IconBadge's `dark:invert`. Keep new
 * additions monochrome — a coloured fill inverts into the wrong colour rather
 * than into its counterpart.
 */

// Content and practice
import breathworkIcon from '../../../assets/svg/icons/breathwork.svg';
import shoorahPodsIcon from '../../../assets/svg/icons/shoorahPods.svg';
import sleepIcon from '../../../assets/svg/icons/sleep_tracker.svg';
import meditationIcon from '../../../assets/svg/icons/meditation.svg';
import shoorahVidsIcon from '../../../assets/svg/icons/shoorahVids.svg';
import guidedPathIcon from '../../../assets/svg/icons/guided_path.svg';
import selfDevelopmentIcon from '../../../assets/svg/icons/self_development.svg';

// Tracking and tools
import habitTrackerIcon from '../../../assets/svg/icons/habit_tracker.svg';
import shuruIcon from '../../../assets/svg/icons/shuru.svg';
import peapIcon from '../../../assets/svg/icons/support_line.svg';
import moodTrackingIcon from '../../../assets/svg/icons/mood_tracking.svg';
import visionBoardIcon from '../../../assets/svg/icons/vision_board.svg';
import gamesIcon from '../../../assets/svg/icons/game.svg';
import eliteMindIcon from '../../../assets/svg/icons/elite_mind.svg';
import moneyMindIcon from '../../../assets/svg/icons/money_mind.svg';

// Health testing
import selfAssessmentIcon from '../../../assets/svg/icons/self_assessment.svg';
import homeTestingIcon from '../../../assets/svg/icons/home_testing.svg';
import insightTestIcon from '../../../assets/svg/icons/insight_tests.svg';
import skinScanIcon from '../../../assets/svg/icons/skin_scan.svg';
import teethScanIcon from '../../../assets/svg/icons/teeth_scan.svg';
import ageScanIcon from '../../../assets/svg/icons/age_scan.svg';

// Care programmes
import glp1Icon from '../../../assets/svg/icons/weight_scale.svg'; // the programme is weight management; the syringe reads as Peptide Care
import pauseIcon from '../../../assets/svg/icons/pause.svg';
import peptideCareIcon from '../../../assets/svg/icons/peptide.svg';
import menstrualCareIcon from '../../../assets/svg/icons/menstrual.svg';
import fertilityCareIcon from '../../../assets/svg/icons/fertility.svg';
import fibromyalgiaCareIcon from '../../../assets/svg/icons/fibro.svg';

// Habit change
import triggerTraceIcon from '../../../assets/svg/icons/triggerTrace.svg';
import lastPuffIcon from '../../../assets/svg/icons/last_puff.svg';
import lastDrinkIcon from '../../../assets/svg/icons/last_drink.svg';

// Company features
import benefitsIcon from '../../../assets/svg/icons/benefits.svg';
import perksIcon from '../../../assets/svg/icons/perks.svg';
import challengesIcon from '../../../assets/svg/icons/challenges.svg';
import shoutoutsIcon from '../../../assets/svg/icons/shoutouts.svg';
import marketplaceIcon from '../../../assets/svg/icons/marketplace.svg';
import badgesIcon from '../../../assets/svg/icons/badges.svg';
import pulseSurveysIcon from '../../../assets/svg/icons/pulse_surveys.svg';

export const FEATURE_ICONS = {
  // Content and practice
  breathwork: breathworkIcon,
  shoorah_pods: shoorahPodsIcon,
  sleep: sleepIcon,
  meditation: meditationIcon,
  shoorah_vids: shoorahVidsIcon,
  guided_path: guidedPathIcon,
  self_development: selfDevelopmentIcon,

  // Tracking and tools
  habit_tracker: habitTrackerIcon,
  shuru: shuruIcon,
  peap: peapIcon, // the support line the programme is built around
  mood_tracking: moodTrackingIcon,
  vision_board: visionBoardIcon,
  game_tracking: gamesIcon,
  elite_mind: eliteMindIcon,
  moneymind: moneyMindIcon,

  // Health testing
  self_assessment: selfAssessmentIcon,
  home_testing: homeTestingIcon,
  insight_test: insightTestIcon,
  skin_scan: skinScanIcon,
  teeth_scan: teethScanIcon,
  age_scan: ageScanIcon,

  // Care programmes
  glp_1: glp1Icon,
  pause: pauseIcon,
  peptide_care: peptideCareIcon,
  menstrual_care: menstrualCareIcon,
  fertility_care: fertilityCareIcon,
  fibromyalgia_care: fibromyalgiaCareIcon,

  // Habit change
  trigger_trace: triggerTraceIcon,
  last_puff: lastPuffIcon,
  last_drink: lastDrinkIcon,

  // Company features
  benefits: benefitsIcon,
  perks: perksIcon,
  challenges: challengesIcon,
  shoutouts: shoutoutsIcon,
  marketplace: marketplaceIcon,
  badges: badgesIcon,
  pulse_surveys: pulseSurveysIcon
};

export default FEATURE_ICONS;
